import React from "react";
import { useIntl } from "react-intl";

import { ButtonLinkIcon } from "@components/Button";
import Card from "@components/Card";
import ConfirmationPopup from "@components/ConfirmationPopup";
import DataTable from "@components/DataTableV2/DataTable";
import Div from "@components/Div";
import { H3, H4 } from "@components/Heading";
import Icon from "@components/Icon";
import ProgressSpinner from "@components/ProgressSpinner";
import PromptDialog from "@components/PromptDialog";
import { TextMediumWeight } from "@components/Text";

import { useCrimesTab } from "./CrimesTab.hooks";
import { CrimesForm } from "./components";

const CrimesTab = () => {
  const {
    totalScore,
    listOfCrimesConfig,
    dismissedCrimesConfig,
    indictmentsConfig,
    promptDialogData,
    crimeToDelete,
    crimesFormConfig,
    isCrimesFormVisible,
    handleClosePromptDialog,
    handleOpenAddCrimeDialog,
    handleCloseDeleteCrimeDialog,
    handleConfirmDeleteCrime,
    isCrimesEditable,
    isIndictmentsVisible,
    isCrimesLoading,
  } = useCrimesTab();
  const { formatMessage, messages } = useIntl();

  return (
    <Div display="flex" flexDirection="column" gridGap={4}>
      {isCrimesLoading && <ProgressSpinner />}

      {promptDialogData !== null && (
        <PromptDialog
          {...promptDialogData}
          onConfirm={handleClosePromptDialog}
        />
      )}

      {crimeToDelete !== null && (
        <ConfirmationPopup
          title={messages.label_delete_crime}
          description={`${formatMessage({ id: "label_delete_crime" })} ${crimeToDelete?.crime_name}`}
          onAcceptClick={handleConfirmDeleteCrime}
          onCancelClick={handleCloseDeleteCrimeDialog}
        />
      )}

      {isCrimesFormVisible && <CrimesForm {...crimesFormConfig} />}

      <Card borderRadius="10px" p={3}>
        <Div
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gridGap="12px"
        >
          <H4>{messages.title_total_score}:</H4>

          <H4>{totalScore}</H4>
        </Div>
      </Card>

      <Div display="flex" flexDirection="column" gridGap={3}>
        <Div display="flex" flexDirection="column" gridGap={2}>
          <H3 textTransform="none">{messages.title_list_of_crimes}</H3>

          <Div
            display="flex"
            gridGap="10px"
            alignItems="center"
            justifyContent="space-between"
          >
            <TextMediumWeight>{messages.text_add_edit_crimes}</TextMediumWeight>

            {isCrimesEditable && (
              <ButtonLinkIcon p={0} onClick={handleOpenAddCrimeDialog}>
                <Icon
                  name="plus"
                  mr={1}
                  color="var(--turquoise)"
                  fontSize="var(--fs-icon-m)"
                />

                <TextMediumWeight>{messages.text_add}</TextMediumWeight>
              </ButtonLinkIcon>
            )}
          </Div>

          <DataTable config={listOfCrimesConfig} />
        </Div>

        <Div display="flex" flexDirection="column" gridGap={2}>
          <H3 textTransform="none">{messages.title_dismissed_crimes}</H3>

          <Div
            display="flex"
            gridGap="10px"
            alignItems="center"
            justifyContent="space-between"
          >
            <TextMediumWeight>{messages.text_add_edit_crimes}</TextMediumWeight>

            {isCrimesEditable && (
              <ButtonLinkIcon p={0} onClick={handleOpenAddCrimeDialog}>
                <Icon
                  name="plus"
                  mr={1}
                  color="var(--turquoise)"
                  fontSize="var(--fs-icon-m)"
                />

                <TextMediumWeight>{messages.text_add}</TextMediumWeight>
              </ButtonLinkIcon>
            )}
          </Div>

          <DataTable config={dismissedCrimesConfig} />
        </Div>

        {isIndictmentsVisible && (
          <Div display="flex" flexDirection="column" gridGap={2}>
            <H3 textTransform="none">{messages.label_indictment}</H3>

            <Div
              display="flex"
              gridGap="10px"
              alignItems="center"
              justifyContent="space-between"
            >
              <TextMediumWeight>
                {messages.text_add_edit_crimes}
              </TextMediumWeight>

              {isCrimesEditable && (
                <ButtonLinkIcon p={0} onClick={handleOpenAddCrimeDialog}>
                  <Icon
                    name="plus"
                    mr={1}
                    color="var(--turquoise)"
                    fontSize="var(--fs-icon-m)"
                  />

                  <TextMediumWeight>{messages.text_add}</TextMediumWeight>
                </ButtonLinkIcon>
              )}
            </Div>

            <DataTable config={indictmentsConfig} />
          </Div>
        )}
      </Div>
    </Div>
  );
};

export default CrimesTab;
